import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/config";

export type DropConnectionTarget = {
  storeId: string;
  stationId: string;
  name: string;
};

function callableMessage(error: unknown) {
  return error instanceof Error ? error.message : "Request failed";
}

const DropConnectionDialog: React.FC<{
  target: DropConnectionTarget | null;
  onClose: () => void;
  onDropped: () => void;
}> = ({ target, onClose, onDropped }) => {
  const toast = useToast();
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setConfirmation("");
  }, [target?.storeId, target?.stationId]);

  const drop = async () => {
    if (!target || confirmation.trim() !== "DROP") return;
    setBusy(true);
    try {
      const call = httpsCallable(functions, "dropWorkstationConnection");
      await call({
        storeId: target.storeId,
        stationId: target.stationId,
        confirmation: confirmation.trim(),
      });
      toast({
        title: "Workstation connection dropped",
        description: "Someone must sign in with the store account on that computer to connect again.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onDropped();
      onClose();
    } catch (error) {
      toast({
        title: "Could not drop the connection",
        description: callableMessage(error),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={Boolean(target)} onClose={busy ? () => undefined : onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Drop workstation connection</ModalHeader>
        <ModalBody>
          <Text fontSize="sm" mb={3}>
            {target?.name || "This workstation"}. This workstation will be signed out. The saved connection will stop working. Someone must sign in with the store account on that computer to connect again.
          </Text>
          <Text fontSize="sm" mb={4}>
            The workstation stays approved. This does not deactivate it.
          </Text>
          <FormControl isRequired>
            <FormLabel>Type DROP to drop this connection</FormLabel>
            <Input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="off" />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={busy}>Cancel</Button>
          <Button colorScheme="blue" isLoading={busy} isDisabled={confirmation.trim() !== "DROP"} onClick={drop}>
            Drop connection
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DropConnectionDialog;
